import * as React from "react";
import Datetime from "react-datetime";
import * as moment from "moment";
import { Translations } from "ui/Scripts/source/models/translations";
import { Base } from "ui/Scripts/source/framework/base";
import { Dialog } from "../framework/dialog";
import { IEndlessList, EndlessList } from "ui/Scripts/source/models/common/endlessList";
import { IIdCode, IdCode } from "../../models/common/idCode";
import { EventStateLine, SelectEventStateDialog } from "./selectEventStateDialog";
import { SelectUnitDialog, SelectUnitDialogLine } from "./selectUnitDialog";
import { IUnitItem, UnitItem } from "../../models/unit/unitItem";
import { DialogButtonType, EventState } from "ui/Scripts/source/models/enums";
import { FormRow } from "../framework/formRow";
import { FormGroup } from "../framework/formGroup";

//EventListSearchDialog
//***************************************************************
export interface IEventListSearchDialogProp {
    state: number;
    startDate: number;
    endDate: number;
    unitId: string;
    units: IUnitItem[];
    companyLogo: string;
    classes?: string;
    onOk: (state: number, startDate: number, endDate: number, unitId: string) => void;
    onCancel: () => void;
}

export interface IEventListSearchDialogState {
    units: IUnitItem[];
    unitsHaveComment: boolean;
    state: string;
    startDate: string;
    endDate: string;
    unitId: string;
    showEventStateDialog: boolean;
    showEventUnitDialog: boolean;
}

export class EventListSearchDialog extends React.Component<IEventListSearchDialogProp, IEventListSearchDialogState> {
    private getEventStates(): IIdCode[] {
        return [{ id: "0", code: "'" + Translations.All + "'" },
            { id: "1", code: Translations.NotApproved },
            { id: "2", code: Translations.Approved },
            { id: EventState.Draft.toString(10), code: Translations.Draft },
            { id: EventState.NotAssigned.toString(10), code: Translations.NotAssigned },
            { id: EventState.Open.toString(10), code: Translations.StateOpen },
            { id: EventState.Closed.toString(10), code: Translations.Closed }];
    }

    private getEventStateById(stateId: string): IIdCode {
        const eventStates = this.getEventStates();
        let eventState = eventStates.find(i => i.id === stateId);
        if (Base.isNullOrUndefined(eventState)) {
            eventState = eventState[0];
        }
        return eventState;
    }

    private getUnits(props: IEventListSearchDialogProp): IUnitItem[] {
        const result: IUnitItem[] = [];
        const allUnit = new UnitItem();
        allUnit.id = Base.emptyGuid;
        allUnit.name = "'" + Translations.All + "'";
        result.push(allUnit);
        for (let i = 0; i < props.units.length; i++) {
            result.push(new UnitItem(props.units[i]));
        }
        return result;
    }

    private getUnitById(units: IUnitItem[], unitId: string): IUnitItem {
        let unit = units.find(i => i.id === unitId);
        if (Base.isNullOrUndefined(unit)) {
            unit = units[0];
        }
        return unit;
    }

    constructor(props) {
        super(props);
        const units = this.getUnits(props);
        this.state = {
            units,
            unitsHaveComment: units.findIndex(i => !Base.isNullOrEmpty(i.comment)) > -1,
            state: props.state.toLocaleFixed(0),
            startDate: Base.utcTimeToDateStr(props.startDate),
            endDate: Base.utcTimeToDateStr(props.endDate),
            unitId: props.unitId,
            showEventStateDialog: false,
            showEventUnitDialog: false
        };
    }

    handleUnitClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ showEventUnitDialog: true });
    };

    handleLoadUnits = (page: number): Promise<IEndlessList<IUnitItem>> => {
        const obj = this;
        return new Promise<IEndlessList<IUnitItem>>((resolve, reject) => {
            const result = new EndlessList<IUnitItem>(null, UnitItem);
            result.items = obj.state.units;
            result.page = 1;
            result.hasMore = false;
            resolve(result);
        });
    };

    handleSelectUnitDialogOk = (id: string, code: string) => {
        this.setState({
            unitId: id,
            showEventUnitDialog: false
        });
    };

    handleSelectUnitDialogCancel = () => {
        this.setState({
            showEventUnitDialog: false
        });
    };

    handleStateClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ showEventStateDialog: true });
    };

    handleLoadEventStates = (page: number): Promise<IEndlessList<IdCode>> => {
        const obj = this;
        return new Promise<IEndlessList<IdCode>>((resolve, reject) => {
            const result = new EndlessList<IdCode>(null, IdCode);
            result.items = obj.getEventStates();
            result.page = 1;
            result.hasMore = false;
            resolve(result);
        });
    };

    handleSelectEventStateDialogOk = (id: string, code: string) => {
        this.setState({
            state: id,
            showEventStateDialog: false
        });
    };

    handleSelectEventStateDialogCancel = () => {
        this.setState({
            showEventStateDialog: false
        });
    };

    handleStartDateChange = (value: moment.Moment | string) => {
        if (typeof value === "string") {
            if (Base.isNullOrEmpty(value)) {
                this.setState({ startDate: "" });
            }
        } else {
            this.setState({ startDate: Base.utcTimeToDateStr(value) });
        }
    };

    handleEndDateChange = (value: moment.Moment | string) => {
        if (typeof value === "string") {
            if (Base.isNullOrEmpty(value)) {
                this.setState({ endDate: "" });
            }
        } else {
            this.setState({ endDate: Base.utcTimeToDateStr(value) });
        }
    };

    handleStartDateBlur = (value: moment.Moment | string) => {
        if (typeof value === "string") {
            this.setState({ startDate: Base.utcTimeToDateStr(value.toUtcDate().getTime()) });
        } else {
            this.setState({ startDate: Base.utcTimeToDateStr(value) });
        }
    };

    handleEndDateBlur = (value: moment.Moment | string) => {
        if (typeof value === "string") {
            this.setState({ endDate: Base.utcTimeToDateStr(value.toUtcDate().getTime()) });
        } else {
            this.setState({ endDate: Base.utcTimeToDateStr(value) });
        }
    };

    handleOkClick = () => {
        this.props.onOk(this.state.state.toInteger(), !Base.isNullOrEmpty(this.state.startDate) ? this.state.startDate.toUtcDate().getTime() : null, !Base.isNullOrEmpty(this.state.endDate) ? this.state.endDate.toUtcDate().getTime() : null,
            !Base.isNullOrEmpty(this.state.unitId) && this.state.unitId !== Base.emptyGuid ? this.state.unitId : null);
    };

    handleCancelClick = () => {
        this.props.onCancel();
    };

    handleClearClick = () => {
        this.setState({ state: "0", startDate: "", endDate: "", unitId: "" });
    };

    render() {
        const props = this.props;
        const state = this.state;
        const units = state.units;
        const eventUnit = this.getUnitById(units, state.unitId);
        const eventState = this.getEventStateById(state.state);
        const dialogClasses = "eventListSearchDialog" + (!Base.isNullOrEmpty(props.classes) ? " " + props.classes : "");
        return (
            <Dialog
                classes={dialogClasses}
                title={Translations.SearchEvents}
                body={
                    <div>
                        {this.props.units.length > 1 &&
                            <FormRow
                                label={Translations.Unit}
                                white={true}
                            >
                                <SelectUnitDialogLine
                                    classes={"form-control unitLine"}
                                    item={eventUnit}
                                    companyLogo={props.companyLogo}
                                    unitsHaveComment={state.unitsHaveComment}
                                    selected={false}
                                    onClick={(id: string) => { this.setState({ showEventUnitDialog: true }); }}
                                />
                            </FormRow>
                        }
                        <FormRow
                            label={Translations.State}
                            white={true}
                        >
                            <div className="form-control" onClick={this.handleStateClick}><EventStateLine item={eventState} /></div>
                        </FormRow>

                        <div className="row">
                            <div className="col-6">
                                <FormGroup
                                    label={Translations.SearchStartDate}
                                >
                                    <Datetime
                                        locale={appConfig.culture}
                                        className={"roWhite"}
                                        value={state.startDate}
                                        dateFormat={"D.M.YYYY"}
                                        timeFormat={false}
                                        closeOnSelect={true}
                                        inputProps={{ maxLength: 10, readOnly: true, disabled: false }}
                                        onChange={this.handleStartDateChange}
                                        onBlur={this.handleStartDateBlur}
                                        renderInput={ (dtProps) => {
                                            function clear() {
                                                dtProps.onChange({ target: { value: "" } });
                                            }
                                            return (
                                                <div className="input-group">
                                                    <input {...dtProps} />
                                                    <div className="input-group-append">
                                                        <button className="btn btn-default" type="button" onClick={clear}>X</button>
                                                    </div>
                                                </div>
                                            );
                                        }}
                                    />
                                </FormGroup>
                            </div>
                            <div className="col-6">
                                <FormGroup
                                    label={Translations.SearchEndDate}
                                >
                                    <Datetime
                                        locale={appConfig.culture}
                                        className={"roWhite right"}
                                        value={state.endDate}
                                        dateFormat={"D.M.YYYY"}
                                        timeFormat={false}
                                        closeOnSelect={true}
                                        inputProps={{ maxLength: 10, readOnly: true, disabled: false }}
                                        onChange={this.handleEndDateChange}
                                        onBlur={this.handleEndDateBlur}
                                        renderInput={ (dtProps) => {
                                            function clear() {
                                                dtProps.onChange({ target: { value: "" } });
                                            }
                                            return (
                                                <div className="input-group">
                                                    <input {...dtProps} />
                                                    <div className="input-group-append">
                                                        <button className="btn btn-default" type="button" onClick={clear}>X</button>
                                                    </div>
                                                </div>
                                            );
                                        }}
                                    />
                                </FormGroup>
                            </div>
                        </div>
                        {state.showEventStateDialog &&
                            <SelectEventStateDialog
                                title={Translations.SelectState}
                                selectedId={state.state}
                                loadIdCodes={this.handleLoadEventStates}
                                onOk={this.handleSelectEventStateDialogOk}
                                onCancel={this.handleSelectEventStateDialogCancel}
                            />
                        }
                        {state.showEventUnitDialog &&
                            <SelectUnitDialog
                                title={Translations.SelectUnit}
                                selectedId={state.unitId}
                                companyLogo={props.companyLogo}
                                loadUnits={this.handleLoadUnits}
                                onOk={this.handleSelectUnitDialogOk}
                                onCancel={this.handleSelectUnitDialogCancel}
                            />
                        }
                    </div>
                }
                buttons={[
                    { title: Translations.Clear, buttonType: DialogButtonType.Default, classes: "mr-auto", onClick: this.handleClearClick },
                    { title: Translations.OK, buttonType: DialogButtonType.Primary, onClick: this.handleOkClick },
                    { title: Translations.Cancel, buttonType: DialogButtonType.Default, onClick: this.handleCancelClick }
                ]}
                onClose={this.handleCancelClick}/>
        );
    }
}